// components/TZ/ext/TrademarkCard.jsx
import React, { useMemo, useState } from 'react';
import { Card, Tooltip, Button } from 'antd';
import { FileSearchOutlined, StarOutlined, StarFilled, InfoCircleOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { addFavorite, removeFavorite } from '../../../store/tz/reducer';
import { v4 as uuidv4 } from 'uuid';
import { getEnrichmentExtraInfo } from './enrichmentUtils';
import { getDocumentStatusDisplay } from '../../../utils/helpers';
import { useTheme } from '../../../theme/ThemeProvider';
import { getScoreCardWrapperStyle, getScoreCardInnerStyle } from '../../../theme/scoreBorder';

const { Meta } = Card;

/**
 * Универсальная карточка товарного знака (документ / заявка).
 *
 * cardType        – 'document' | 'request'. Если не передан, пытаемся определить автоматически.
 * document        – словарь полей, полученный парсером.
 * imgSrc          – URL / base64 изображения (может быть null).
 * onClick         – обработчик клика по карточке.
 * score           – рейтинг похожести (только для «document»; влияет на градиент).
 * extraFields     – дополнительные поля (только для «document»).
 * searchQuery     – исходный поисковый запрос (для избранного).
 * coverHeight     – высота изображения (px, по‑умолчанию 150).
 * coverWidth      – ширина карточки (число | 'auto', по‑умолчанию 'auto').
 * descriptionVariant – 'default' | 'semantic': при 'semantic' в описании показывается document.semantic_description вместо страны/классов.
 */

const DocumentCard = ({
  cardType,
  document,
  imgSrc,
  onClick,
  score,
  extraFields = [],
  searchQuery = '',
  coverHeight = 150,
  coverWidth = 'auto',
  csvEnrichment: propCsvEnrichment,
  descriptionVariant = 'default',
}) => {
  const [showEnrichment, setShowEnrichment] = useState(false);

  // ------------------------------------------------------- определяем тип карточки
  const type = useMemo(() => {
    if (cardType) return cardType; // явно передан
    // если есть поле 210 – считаем «заявкой», иначе «документом»
    return document?.['210_Номер_заявки'] ? 'request' : 'document';
  }, [cardType, document]);

  // ------------------------------------------------------- Redux + избранное
  const dispatch  = useDispatch();
  const favorites = useSelector((state) => state.tz.favorites);

  // Универсальный ID: берём поле 111 (документ) или 210 (заявка); иначе генерируем UUID
  const cardId = useMemo(() => {
    if (document?.['111_numReg'])          return '' + document['111_numReg'];
    if (document?.['210_Номер_заявки'])    return ''+ document['210_Номер_заявки'];
    return uuidv4();
  }, [document]);

  const isFavorite = favorites.some((fav) => fav.id === cardId);

  const toggleFavorite = (e) => {
    e.stopPropagation();
    if (isFavorite) {
      dispatch(removeFavorite(cardId));
    } else {
      // Для заявок score может быть в document.score или в пропсе score
      const finalScore = score !== undefined && score !== null ? score : 
                        (document?.score !== undefined && document?.score !== null ? document.score : null);
      
      dispatch(
        addFavorite({
          id: cardId,
          searchQuery,
          document,
          imgSrc,
          score: finalScore,
          request_type: type === 'document' ? 'documents' : 'requests',
        }),
      );
    }
  };

  // ------------------------------------------------------- классы МКТУ
  const rawClasses =
    type === 'document'
      ? document?.['511_KlassyMKTU'] ||
        document?.['Nice Cl.'] ||
        document?.['MARKGR/CURRENT/BASICGS@NICEVER']
      : document?.['511_Классы_МКТУ_и_перечень_товаров_и/или_услуг'] ||
        document?.['511_KlassyMKTU'];

  const mktuList = rawClasses ? Array.from(new Set(String(rawClasses).match(/\d+/g))) : null;
  const mktuShort =
    mktuList && mktuList.length > 3 ? `${mktuList.slice(0, 3).join(', ')}…` : mktuList?.join(', ') || '—';

  // enrichment logic
  const csvEnrichment = typeof propCsvEnrichment !== 'undefined' ? propCsvEnrichment : document?.csvEnrichment;
  let enrichmentExtra = null;
  if (type === 'document' && csvEnrichment) {
    enrichmentExtra = getEnrichmentExtraInfo(csvEnrichment);
  }

  // Проверка на истекший срок действия
  const isExpired = useMemo(() => {
    const expirationDate =
      csvEnrichment?.expiration_date ||
      document?.expiration_date ||
      document?.['181_dataIstIsklPrava'];
    if (!expirationDate) return false;
    // Обрабатываем разные форматы дат
    let dateToParse = expirationDate;
    // Если дата в массиве, берем первый элемент
    if (Array.isArray(expirationDate)) {
      dateToParse = expirationDate[0];
    }
    // Если дата в формате DD.MM.YYYY, преобразуем в YYYY-MM-DD
    if (typeof dateToParse === 'string' && dateToParse.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
      const [day, month, year] = dateToParse.split('.');
      dateToParse = `${year}-${month}-${day}`;
    }
    const expiration = new Date(dateToParse);
    const now = new Date();
    if (isNaN(expiration.getTime())) {
      return false;
    }
    return expiration < now;
  }, [document, csvEnrichment]);

  // СТАРЫЙ стиль и логика статуса
  let statusText = '';
  let statusStyle = {
    textAlign: 'center',
    fontWeight: 'bold',
    backgroundColor: '',
    color: '#fff',
    fontSize: 14,
    borderRadius: 4,
    padding: '2px 8px',
    marginBottom: 4,
  };
  if (csvEnrichment && typeof csvEnrichment.actual !== 'undefined') {
    // вычисляем expiration
    const expirationDate = csvEnrichment.expiration_date;
    let dateToParse = expirationDate;
    if (Array.isArray(expirationDate)) dateToParse = expirationDate[0];
    if (typeof dateToParse === 'string' && dateToParse.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
      const [day, month, year] = dateToParse.split('.');
      dateToParse = `${year}-${month}-${day}`;
    }
    const expiration = dateToParse ? new Date(dateToParse) : null;
    const now = new Date();
    if (csvEnrichment.actual === true) {
      if (expiration && !isNaN(expiration.getTime()) && expiration < now) {
        statusText = 'ИСТЕК/ИСТЕКАЕТ СРОК!';
        statusStyle.backgroundColor = 'rgba(158, 152, 102, 0.8)';
        // statusStyle.animation = 'blink 1.5s infinite';
      } else {
        statusText = 'Действует';
        statusStyle.backgroundColor = 'rgba(40,167,69,0.6)';
        delete statusStyle.animation;
      }
    } else {
      statusText = 'Не действует';
      statusStyle.backgroundColor = 'rgba(108,117,125,0.6)';
      delete statusStyle.animation;
    }
  } else if (type === 'request') {
    const raw = getDocumentStatusDisplay(document);
    if (raw) {
      statusText = raw.length > 100 ? `${raw.slice(0, 97)}…` : raw;
      statusStyle.backgroundColor = 'rgba(73, 80, 87, 0.88)';
      statusStyle.fontSize = raw.length > 48 ? 11 : 14;
      statusStyle.lineHeight = 1.25;
      statusStyle.padding = raw.length > 48 ? '4px 6px' : '2px 8px';
    } else if (isExpired) {
      statusText = 'ИСТЕК/ИСТЕКАЕТ СРОК!';
      statusStyle.backgroundColor = 'rgba(158, 152, 102, 0.8)';
    } else {
      statusText = '';
    }
  } else if (isExpired) {
    statusText = 'ИСТЕК/ИСТЕКАЕТ СРОК!';
    statusStyle.backgroundColor = 'rgba(158, 152, 102, 0.8)';
    // statusStyle.animation = 'blink 1.5s infinite';
  } else {
    statusText = 'Действует';
    statusStyle.backgroundColor = 'rgba(40,167,69,0.6)';
    delete statusStyle.animation;
  }

  // ------------------------------------------------------- градиент / рамка по score
  const { darkMode } = useTheme();

  const containerStyle = getScoreCardWrapperStyle(score, darkMode, {
    width: typeof coverWidth === 'number' ? `${coverWidth}px` : coverWidth,
  });

  const cardStyle = getScoreCardInnerStyle(darkMode, {
    borderRadius: 6,
    width: '100%',
    height: showEnrichment ? '330px' : 'auto',
    overflow: showEnrichment ? 'visible' : 'visible',
    transition: 'height 0.3s ease',
  });

  // ------------------------------------------------------- заголовок
  const titleText =
    document?.realName ||
    document?.Trademark ||
    document?.['MARKGR/CURRENT/IMAGE@TEXT'] ||
    document?.['MARKGR/CURRENT/REPGR/NAME'] ||
    document?.['@INTREGN'] ||
    'Без названия';

  // ------------------------------------------------------- рендер

  return (
    <div style={containerStyle}>
      <Card
        hoverable
        bordered={false}
        cover={
          !showEnrichment ? (
            <div style={{ cursor: 'pointer' }} onClick={onClick}>
              {imgSrc ? (
                <img
                  src={imgSrc}
                  alt={titleText}
                  style={{
                    width: '100%',
                    height: coverHeight,
                    objectFit: 'contain',
                    borderTopLeftRadius: 6,
                    borderTopRightRadius: 6,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: coverHeight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FileSearchOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                </div>
              )}
              {/* цветовая плашка статуса (для заявок — из делопроизводства, без ложного «Действует») */}
              {statusText ? <div style={statusStyle}>{statusText}</div> : null}
            </div>
          ) : null
        }
        style={cardStyle}
      >
        <Meta
          title={
            showEnrichment ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Button
                  type="link"
                  icon={<ArrowLeftOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowEnrichment(false);
                  }}
                  style={{
                    color: '#1890ff',
                    padding: 0,
                    fontSize: 14,
                    height: 'auto'
                  }}
                >
                  Назад
                </Button>
                <span>{titleText}</span>
              </div>
            ) : (
              titleText
            )
          }
          description={
            showEnrichment ? (
              // Показываем enrichment данные в прокручиваемом контейнере
              <div className="theme-preview-muted" style={{
                height: '240px',
                overflowY: 'auto',
                marginTop: '12px',
                padding: '12px',
                borderRadius: 4,
              }}>
                {enrichmentExtra && enrichmentExtra.map(item => (
                  <div key={item.key} style={{ fontSize: 13, marginBottom: 6 }}>
                    <b>{item.label}:</b>{' '}
                    {item.key === 'publication_url' && item.value ? (
                      <a href={item.value} target="_blank" rel="noopener noreferrer">{item.value}</a>
                    ) : (
                      item.value || '—'
                    )}
                  </div>
                ))}
              </div>
            ) : descriptionVariant === 'semantic' ? (
              // Поиск по описанию: semantic_description + классы МКТУ
              <>
                {(document?.semantic_description || '').trim() && (
                  <div
                    style={{
                      fontSize: 13,
                      lineHeight: 1.5,
                      maxHeight: 100,
                      overflowY: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      marginBottom: 6,
                    }}
                  >
                    {String(document.semantic_description).trim()}
                  </div>
                )}
                <div style={{ marginBottom: 2 }}>
                  <b>Классы МКТУ:</b>{' '}
                  {mktuList && mktuList.length > 3 ? (
                    <Tooltip title={mktuList.join(', ')}>{mktuShort}</Tooltip>
                  ) : (
                    mktuShort
                  )}
                </div>
              </>
            ) : (
              // Показываем основную информацию
              <>
                {type === 'request' && (
                  <>
                    <div style={{ marginBottom: 2 }}>
                      <b>№ заявки:</b> {document?.['210_Номер_заявки'] || '—'}
                    </div>
                    <div style={{ marginBottom: 2 }}>
                      <b>Дата подачи:</b> {document?.['Дата_из_таблицы']?.[0] || '—'}
                    </div>
                  </>
                )}
                {type === 'document' && (
                  <div style={{ marginBottom: 2 }}>
                    <b>Страна:</b> {document?.['190_country'] || '—'}
                  </div>
                )}
                <div style={{ marginBottom: 2 }}>
                  <b>Классы МКТУ:</b>{' '}
                  {mktuList && mktuList.length > 3 ? (
                    <Tooltip title={mktuList.join(', ')}>{mktuShort}</Tooltip>
                  ) : (
                    mktuShort
                  )}
                </div>
                {/* extraFields*/}
                { extraFields?.length > 0 && (
                  <>
                    {extraFields.map(f => (
                      <div key={f.label} style={{ marginBottom: 2 }}>
                        <b>{f.label}:</b> {document[f.valueKey] || '—'}
                      </div>
                    ))}
                  </>
                )}
                {/* Кнопка для показа enrichment данных */}
                {enrichmentExtra && enrichmentExtra.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Button
                      type="link"
                      icon={<InfoCircleOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowEnrichment(true);
                      }}
                      style={{
                        color: '#1890ff',
                        fontWeight: 500,
                        padding: 0,
                        height: 'auto',
                        fontSize: 13
                      }}
                    >
                      Показать доп. информацию
                    </Button>
                  </div>
                )}
              </>
            )
          }
        />

      {/* иконка избранного */}
      <div
        onClick={toggleFavorite}
        style={{ position: 'absolute', top: 8, right: 8, cursor: 'pointer', zIndex: 1 }}
      >
        {isFavorite ? (
          <StarFilled style={{ color: '#fadb14', fontSize: 24 }} />
        ) : (
          <StarOutlined style={{ color: '#fadb14', fontSize: 24 }} />
        )}
      </div>
      <style>
        {`
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0.5; }
          }
        `}
      </style>
    </Card>
    </div>
  );
};

export default React.memo(DocumentCard);